import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./GroupGrid.css";

const GroupDetailsModal = ({
  group,
  isAdmin,
  isJoined,
  schedules = [],
  onClose,
  onEdit,
  onDelete,
  onJoin,
  onLeave
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  if (!group) return null;

  const formatTime = (time) => {
    if (!time) return "";
    const [hourStr, minuteStr = "00"] = String(time).split(":");
    const parsed = parseInt(hourStr, 10);
    if (Number.isNaN(parsed)) return time;
    let hour = parsed;
    const minutes = minuteStr.padStart(2, "0");
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minutes} ${ampm}`;
  };

  const getFrequencyLabel = (groupData, scheduleList) => {
    if (!groupData || !groupData.recurring) return null;
    const freq = groupData.frequency;
    if (freq === 'weekly') return 'Weekly';
    if (freq === 'biweekly') return 'Every 2 weeks';
    if (freq === 'monthly') return 'Monthly';
    if (typeof freq === 'string' && freq.trim()) {
      const trimmed = freq.trim();
      return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
    }
    if ((scheduleList || []).length > 1) return 'Weekly';
    return 'Weekly';
  };

  const categories = Array.isArray(group.category) ? group.category : [];
  const recurringLabel = getFrequencyLabel(group, schedules);

  return (
    <div className="group-modal-overlay" onClick={onClose}>
      <div className="group-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="gdm-close-btn" type="button" onClick={onClose}>
          ×
        </button>

        {/* Admin kebab menu */}
        {isAdmin && (
          <div className="gdm-kebab-wrapper">
            <button
              className="gdm-kebab-btn"
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="More options"
            >
              ⋮
            </button>
            {menuOpen && (
              <div className="gdm-kebab-menu">
                <button
                  type="button"
                  className="gdm-menu-item"
                  onClick={() => {
                    onEdit(group);
                    onClose();
                  }}
                >
                  Edit Group
                </button>
                <div className="gdm-menu-danger-label">Danger zone</div>
                <button
                  type="button"
                  className="gdm-menu-item danger"
                  onClick={() => {
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this group? This action cannot be undone.'
                    );
                    if (!confirmed) return;
                    onDelete(group.id);
                    onClose();
                  }}
                >
                  Delete Group
                </button>
              </div>
            )}
          </div>
        )}

        {/* Header with banner */}
        <div className="gdm-header">
          {group.group_banner_url ? (
            <img
              src={group.group_banner_url}
              alt={group.name}
              className="gdm-banner"
            />
          ) : (
            <div className="gdm-banner-placeholder">
              <span>{group.name ? group.name[0] : "G"}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="gdm-content">
          <h2 className="gdm-title">{group.name}</h2>

          {/* Info chips */}
          <div className="gdm-info-chips">
            {group.language && (
              <span className="gdm-chip">Language: {group.language}</span>
            )}
            {group.level && (
              <span className="gdm-chip">Level: {group.level}</span>
            )}
            {group.capacity && (
              <span className="gdm-chip">
                Capacity: {group.capacity} members
              </span>
            )}
            {recurringLabel && (
              <span className="gdm-chip">Frequency: {recurringLabel}</span>
            )}
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="gdm-categories">
              {categories.map((cat) => (
                <span className="gdm-category-chip" key={cat}>
                  {cat}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          {group.description && (
            <div className="gdm-description">
              <p>{group.description}</p>
            </div>
          )}

          {/* Schedules */}
          {schedules.length > 0 && (
            <div className="gdm-schedules">
              <h4>Meeting schedule</h4>
              <div className="gdm-schedule-list">
                {schedules.map((sch, idx) => (
                  <div className="gdm-schedule-item" key={sch.id || idx}>
                    <span className="gdm-schedule-day">{sch.day}</span>
                    <span className="gdm-schedule-time">
                      {formatTime(sch.time)}
                    </span>
                    <span className="gdm-schedule-duration">
                      {sch.duration} min
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="gdm-actions">
          {/* Deep-link CTA to the full group About page (new tab) */}
          {group.id && (
            <button
              type="button"
              className="gdm-action-btn secondary"
              onClick={() => {
                onClose();
                window.open(`/app/groups/${group.id}`, '_blank', 'noopener,noreferrer');
              }}
            >
              Go to Group Page
            </button>
          )}

          {(isJoined || isAdmin) && group.room_id && (
            <button
              type="button"
              className="gdm-action-btn secondary"
              onClick={() => {
                navigate(`/app/virtualcafe/${group.room_id}`);
              }}
            >
              Join Virtual Room
            </button>
          )}
          {isJoined ? (
            <>
              <button
                type="button"
                className="gdm-action-btn primary joined"
                disabled
              >
                Joined
              </button>
              <button
                type="button"
                className="gdm-action-btn outline danger-outline"
                onClick={() => {
                  const confirmed = window.confirm(
                    `Leave "${group.name}"? You will need to rejoin to access group content.`
                  );
                  if (!confirmed) return;
                  onLeave(group.id);
                  onClose();
                }}
              >
                Leave Group
              </button>
            </>
          ) : (
            <button
              type="button"
              className="gdm-action-btn primary"
              onClick={() => {
                onJoin(group.id);
                onClose();
              }}
            >
              Join Group
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupDetailsModal;
